import '@babel/polyfill';
import { LicenseManager } from '@ag-grid-enterprise/core'; // eslint-disable-line
import { createInstance, OptimizelyProvider } from '@optimizely/react-sdk'; // eslint-disable-line
import * as Sentry from '@sentry/react';
// imports modules from common package
import {
	Authentication,
	Chatbot,
	Data,
	FeatureFlags,
	Layout,
	Modal,
	Naws,
	Redirect,
	ThinClient,
	ThinClientInterface,
	Toast,
	UI,
	Utils,
} from 'common';
import React, { Component } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter as Router } from 'react-router-dom';
import MainView from './components/MainView.component';
import HistoryCommunication from './components/utils/HistoryCommunication';

const { EndiciaRedirect } = Redirect;

Sentry.init({
	dsn: 'https://2bf0cdb2c7121a2c231715a123b5bbb1@o106853.ingest.us.sentry.io/4509831239499776',
	// Setting this option to true will send default PII data to Sentry.
	// For example, automatic IP address collection on events
	sendDefaultPii: true,
	environment:
		process.env.NODE_ENV === 'production' ? 'history' : 'history-dev',
});

const { CalculatedData } = Data;
const { RegionType, Viewport } = Layout;
const { ModalContainer } = Modal;
const { PluginInterface } = Naws;
const { ChatbotInitializer, SierraInitializer, SierraChatLauncher } = Chatbot;
const { InitializeLaunchDarkly } = FeatureFlags;
const {
	Analytics,
	Auth0Util,
	Session,
	StyleLoader,
	Util,
	withLayoutAndModalContexts,
	Navigation,
	BillingPlans,
} = Utils;
const { Spinner } = UI;
const { SignIn } = Authentication;
const { ToastProvider } = Toast;

// set license for Ag-Grid
LicenseManager.setLicenseKey(__AG_GRID_LICENSE__);

// The higher-order component (HOC) that comes with layoutContext and modalContext objects
const MainViewWithLayoutAndModal = withLayoutAndModalContexts(MainView);

export default class App extends Component {
	constructor(props) {
		super(props);

		const page = 'History';

		const winClientVersion = Util.getQueryParamByName('cver');

		// Check if on Endicia domain - skip auth calls but still load styles
		const redirectToStamps = Util.isEndiciaDomain();

		// initialize Session with sessionTimeout handler
		Session.init(
			{
				Page: page,
			},
			() => {
				const { IsInWinClient, UseAuth0SignIn } = Session.getSettings();
				const { isSso } = Session.getCalculatedSettings();
				const isSafari = Util.getBrowser(true) === 'Safari';

				SierraInitializer.logout();
				ChatbotInitializer.logout();

				if (Auth0Util.isEligibleForAuth0()) {
					Session.signOut(() => {
						Auth0Util.signOut();
					});
				} else {
					this.setState({
						showSignIn: true,
					});
				}
			}
		);

		HistoryCommunication.init({
			resetTimeout: Session.resetTimeout,
			triggerTimeout: Session.triggerTimeout,
		});

		this.state = {
			data: {
				InternalAccountInfo: true,
				winClientVersion,
			},
			page,
			showMask: false,
			showSignIn: false,
			stylesLoaded: false,
			auth0Error: false,
		};

		// Skip verifyAuthAndLoad for Endicia domain, but still load styles
		if (redirectToStamps) {
			StyleLoader.loadTheme(() => {
				// Directly set state in constructor (setState not allowed here)
				this.state.showMask = false;
				this.state.stylesLoaded = true;
			});
		} else {
			this.verifyAuthAndLoadSession();
		}
	}

	sessionLoadCallback = async () => {
		const sessionData = Session.getData();
		const { IsInWinClient } = Session.getSettings();
		const { isSignedIn, settings } = sessionData;
		const { page } = this.state;
		const optimizelyClient = createInstance({ sdkKey: settings.OptimizelyKey });
		const isAccountVerificationRequired =
			Session.isAccountVerificationRequired();

		// update document title
		document.title = `Stamps.com | ${page}`;

		// load the theme bundle
		StyleLoader.loadTheme(
			this.setState({ showMask: true, stylesLoaded: true })
		);

		const completeAuth = Util.isInRoute('completeAuth') === true;

		if (!isSignedIn && completeAuth) {
			const completeAuthResponse = await Auth0Util.completeAuth();

			if (completeAuthResponse?.isSuccessful === true) {
				Navigation.redirect(completeAuthResponse.url);
				return;
			} else {
				this.setState({
					auth0Error: true,
				});
			}
		}

		if (!IsInWinClient && isAccountVerificationRequired) {
			await Navigation.redirectToAccountVerify();
		}

		// initialize analytics
		Analytics.set({
			...sessionData,
			role: Session.getUserRole(),
		});

		const LDProvider = await InitializeLaunchDarkly();

		if (!IsInWinClient) {
		}

		// make a call to carrier-services api
		if (isSignedIn === true) {
			const shipDate = new Date().toISOString().split('T')[0];
			const response = await HistoryCommunication.getCarrierServices(shipDate);
			const data = HistoryCommunication.isSuccessful(response)
				? Util.toCamelCase(response.data)
				: null;

			CalculatedData.init(data);
			BillingPlans.init();
		}

		this.setState({
			showMask: false,
			showSignIn: isSignedIn !== true,
			optimizelyClient,
			analyticsMetadata: Analytics.metadata,
			LDProvider,
		});

		if (ThinClient.isInThinClient()) {
			ThinClientInterface.init();
		} else {
			PluginInterface.init();
		}
	};

	signInCallback = ({ status }) => {
		if (status) {
			this.setState(
				{
					showMask: true,
					showSignIn: false,
				},
				this.verifyAuthAndLoadSession
			);
			ChatbotInitializer.resetChatMessengerUserPrefs();
		}
	};

	verifyAuthAndLoadSession = () => {
		const { data } = this.state;

		Session.verifyAuthAndLoad(data, this.sessionLoadCallback);
	};

	render() {
		// Redirect to Stamps.com when on Endicia domain - must be the very first thing
		const redirectToStamps = Util.isEndiciaDomain();

		if (redirectToStamps) {
			return <EndiciaRedirect />;
		}

		const {
			page,
			showMask,
			showSignIn,
			optimizelyClient,
			analyticsMetadata,
			LDProvider,
			stylesLoaded,
			auth0Error,
		} = this.state;
		const { IsInWinClient, UseAuth0SignIn } = Session.getSettings();
		const { isSso } = Session.getCalculatedSettings();
		const isSafari = Util.getBrowser(true) === 'Safari';

		// layout is applicable for MainView
		const layout = showSignIn
			? null
			: {
					[RegionType.North]: {
						height: 50,
						isHidden: IsInWinClient === true,
					},
					[RegionType.West]: {
						width: 230,
						minWidth: 50,
						maxWidth: 600,
						resizable: true,
						isCollapsed: false,
						isHidden: false,
					},
					[RegionType.East]: {
						width: 425,
						minWidth: 50,
						maxWidth: 600,
						resizable: true,
						isCollapsed: false,
						isHidden: true,
					},
				};

		if (!stylesLoaded) {
			return null;
		}

		if (showMask) {
			return <Spinner msg={`Loading ${page}`} />;
		}

		if (showSignIn) {
			if (auth0Error === false && Auth0Util.isEligibleForAuth0()) {
				Auth0Util.launchSignInPage();
				return null;
			}
			return (
				<Viewport shouldScroll={showSignIn}>
					<ModalContainer>
						<ToastProvider>
							<Router basename={__BASENAME__}>
								<SignIn
									signInCallback={this.signInCallback}
									isAuth0Error={auth0Error}
								/>
							</Router>
						</ToastProvider>
					</ModalContainer>
				</Viewport>
			);
		}

		return (
			<Viewport layout={layout} shouldScroll={showSignIn}>
				<ModalContainer>
					<ToastProvider>
						<OptimizelyProvider
							optimizely={optimizelyClient}
							user={{
								id: analyticsMetadata.visitor.id.toString(),
								attributes: analyticsMetadata.visitor,
							}}
						>
							{LDProvider ? (
								<LDProvider>
									<MainViewWithLayoutAndModal />
									<SierraChatLauncher />
								</LDProvider>
							) : (
								<>
									<MainViewWithLayoutAndModal />
									<SierraChatLauncher />
								</>
							)}
						</OptimizelyProvider>
					</ToastProvider>
				</ModalContainer>
			</Viewport>
		);
	}
}

const root = createRoot(document.getElementById('root'));
root.render(<App />);
